import { Check, LoaderCircle, TriangleAlert, X } from "lucide-react";

export type FaseCriacao = "criando" | "incluindo" | "concluida" | "falha_equipe";

interface EtapasCriacaoEscalaProps {
  fase: FaseCriacao;
  /** Quantas pessoas serão (ou foram) incluídas — 0 = escala sem equipe. */
  quantidade: number;
  /** Texto de fechamento (depende da regra de confirmação e de quem foi avisado) — montado pela página. */
  resumoConcluida: string;
  /** Mensagem devolvida pelo banco quando a inclusão da equipe falhou. */
  erroEquipe: string | null;
  onVerEscala: () => void;
  onCriarOutra: () => void;
  onTentarNovamente: () => void;
}

type EstadoPasso = "espera" | "andamento" | "feito" | "falha";

function Passo({ estado, children }: { estado: EstadoPasso; children: string }) {
  return (
    <li className="flex items-center gap-3 text-[14.5px] text-text">
      <span
        aria-hidden
        className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
          estado === "feito"
            ? "bg-success/15 text-success"
            : estado === "falha"
              ? "bg-danger/10 text-danger"
              : estado === "andamento"
                ? "bg-primary/10 text-primary"
                : "bg-surface-2 text-muted"
        }`}
      >
        {estado === "feito" && <Check size={16} strokeWidth={3} />}
        {estado === "falha" && <X size={16} strokeWidth={3} />}
        {estado === "andamento" && <LoaderCircle size={16} className="animate-spin motion-reduce:animate-none" />}
      </span>
      <span className={estado === "espera" ? "text-muted" : "font-semibold"}>{children}</span>
    </li>
  );
}

/**
 * Andamento da criação de escala com equipe. `escala_criar` e
 * `escala_adicionar_participantes` são duas chamadas em sequência: se a
 * segunda falhar, a escala já existe — por isso a falha da equipe tem tela
 * própria ("a escala foi criada, mas está sem equipe") em vez de parecer que
 * nada foi feito. Sem botão de fechar: enquanto trabalha, não dá para
 * abandonar no meio.
 */
export function EtapasCriacaoEscala({
  fase,
  quantidade,
  resumoConcluida,
  erroEquipe,
  onVerEscala,
  onCriarOutra,
  onTentarNovamente,
}: EtapasCriacaoEscalaProps) {
  const rotuloEquipe = `Incluindo ${quantidade} ${quantidade === 1 ? "pessoa" : "pessoas"} na equipe`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="etapas-criacao-titulo"
        className="flex max-h-full w-full max-w-md flex-col gap-5 overflow-y-auto rounded-lg border border-border bg-surface p-6 shadow-xl"
      >
        {fase === "concluida" ? (
          <>
            <div className="flex flex-col items-center gap-3 pt-2 text-center" role="status">
              <span className="grid h-20 w-20 place-items-center rounded-full bg-success/15 text-success">
                <Check size={42} strokeWidth={2.5} aria-hidden />
              </span>
              <h2 id="etapas-criacao-titulo" className="text-xl font-extrabold text-text">
                Escala criada
              </h2>
              <p className="max-w-xs text-sm text-muted">{resumoConcluida}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={onVerEscala}
                autoFocus
                className="min-h-11 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover"
              >
                Ver a escala
              </button>
              <button
                type="button"
                onClick={onCriarOutra}
                className="min-h-11 rounded-xl px-4 text-[15px] font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-text"
              >
                Criar outra
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="etapas-criacao-titulo" className="text-lg font-extrabold text-text">
              {fase === "falha_equipe" ? "Escala criada, equipe não escalada" : "Criando a escala…"}
            </h2>
            <ol className="flex flex-col gap-3" role="status" aria-live="polite">
              <Passo estado={fase === "criando" ? "andamento" : "feito"}>
                {fase === "criando" ? "Criando a escala" : "Escala criada"}
              </Passo>
              {quantidade > 0 && (
                <Passo estado={fase === "criando" ? "espera" : fase === "incluindo" ? "andamento" : "falha"}>
                  {fase === "falha_equipe" ? "Não foi possível escalar a equipe" : rotuloEquipe}
                </Passo>
              )}
            </ol>
            {fase === "falha_equipe" && (
              <>
                <div className="flex items-start gap-2.5 rounded-xl bg-warning/20 px-3.5 py-3 text-[13.5px] leading-snug text-text">
                  <TriangleAlert size={18} aria-hidden className="mt-px shrink-0 text-warning" />
                  <p className="min-w-0 break-words">
                    <b>A escala foi criada, mas está sem equipe.</b> Nenhuma pessoa foi escalada e ninguém foi avisado.
                    Você pode tentar de novo agora ou escalar depois, dentro da escala.
                    {erroEquipe && <span className="mt-1.5 block text-muted">Detalhe: {erroEquipe}</span>}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={onTentarNovamente}
                    autoFocus
                    className="min-h-11 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover"
                  >
                    Tentar escalar de novo
                  </button>
                  <button
                    type="button"
                    onClick={onVerEscala}
                    className="min-h-11 rounded-xl px-4 text-[15px] font-semibold text-muted transition-colors hover:bg-surface-2 hover:text-text"
                  >
                    Ver a escala sem equipe
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
